import { useQuery } from '@tanstack/react-query';
import { useOrganizationGlobalSearch } from '@hooks/organizations';
import { useIsV2Account } from '@features/page-versions';
import CatalogService from '@utility/services/catalog';
import OrdersService from '@utility/services/order';

/** One section of the search: the legacy search's model name and its rows. */
export type SearchSection = { model: string; response: unknown[] };

const ORDER_MODEL = 'order';
const ITEMS_MODEL = 'items';
const V2_LIMIT = 10;

type V2OrderRow = { marketplace?: unknown } & Record<string, unknown>;
type V2CatalogRow = { id: string; title: string; image_url: string | null };

/* The legacy result row reads the marketplace as a list; a V2 row carries one. */
const asSearchOrder = (row: V2OrderRow) => ({
  ...row,
  marketplace: row.marketplace ? [row.marketplace] : [],
});

/* The legacy result row links by `_id` and draws `p_images`; a V2 catalog row has its uuid and one image. */
const asSearchItem = (row: V2CatalogRow) => ({
  _id: row.id,
  title: row.title,
  p_images: row.image_url ? [row.image_url] : [],
});

const section = (model: string, rows: unknown[]): SearchSection[] =>
  rows.length ? [{ model, response: rows }] : [];

/**
 * The global search's sections. On a V2 account only what the new system
 * serves: its orders (the V2 orders search) and its catalog items (the V2
 * catalog search), each opening a V2 page. The legacy search's other
 * sections (purchase orders, suppliers, wholesale, customers...) lead to
 * legacy pages a V2 account has nothing on, so they are not shown.
 */
export function useGlobalSearchResults(search: string) {
  const isV2 = useIsV2Account();
  const legacy = useOrganizationGlobalSearch(isV2 ? '' : search);
  const term = search.trim();
  const enabled = isV2 && term.length >= 3;
  const orders = useQuery<{ data?: V2OrderRow[] }>({
    queryKey: ['global-search-orders', term],
    queryFn: () =>
      OrdersService.get({ search: term, page: 1, limit: V2_LIMIT }),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
  const items = useQuery<{ source?: string; data?: unknown }>({
    queryKey: ['global-search-items', term],
    queryFn: () =>
      CatalogService.mergedData({ search: term, page: 1, limit: V2_LIMIT }),
    enabled,
    retry: false,
    staleTime: 30_000,
  });

  if (!isV2) {
    return {
      loading: legacy.loading,
      data: (legacy.data ?? []) as SearchSection[],
    };
  }

  const itemRows =
    items.data?.source === 'v2' ? (items.data.data as V2CatalogRow[]) : [];
  return {
    loading: orders.isLoading || items.isLoading,
    data: [
      ...section(ORDER_MODEL, (orders.data?.data ?? []).map(asSearchOrder)),
      ...section(ITEMS_MODEL, (itemRows ?? []).map(asSearchItem)),
    ],
  };
}
